import React from 'react';
import { motion } from 'framer-motion';

// Motion vocabulary primitives for the marketing surface (nuraform-inspired).
// CSS-first utilities (.reveal-up / .motif-float) exist for lightweight cases;
// these Framer wrappers cover staggered + word-by-word reveals.
//
// Pass `now` for above-the-fold content (hero) so it animates on mount instead
// of relying on whileInView — the IntersectionObserver trigger is unreliable for
// elements already inside the viewport at initial render.

const EASE = [0.16, 1, 0.3, 1];

// Fade/slide up. Scroll-triggered by default; `now` = animate immediately on mount.
export function Reveal({ children, delay = 0, y = 24, className = '', style, as = 'div', now = false }) {
  const MotionTag = motion[as] || motion.div;
  const trigger = now
    ? { animate: { opacity: 1, y: 0 } }
    : { whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.15 } };
  return (
    <MotionTag
      className={className}
      style={style}
      initial={{ opacity: 0, y }}
      {...trigger}
      transition={{ duration: 0.6, ease: EASE, delay }}
    >
      {children}
    </MotionTag>
  );
}

// Container that staggers its <Reveal>/motion children.
export function RevealGroup({ children, stagger = 0.08, className = '', style, now = false }) {
  const trigger = now
    ? { animate: 'show' }
    : { whileInView: 'show', viewport: { once: true, amount: 0.15 } };
  return (
    <motion.div
      className={className}
      style={style}
      initial="hidden"
      {...trigger}
      variants={{ show: { transition: { staggerChildren: stagger } } }}
    >
      {children}
    </motion.div>
  );
}

// Word-by-word headline reveal. Pass a string; each word animates in sequence.
export function RevealWords({ text, className = '', stagger = 0.06, delay = 0, now = false }) {
  const words = String(text).split(' ');
  const trigger = now
    ? { animate: 'show' }
    : { whileInView: 'show', viewport: { once: true, amount: 0.4 } };
  return (
    <motion.span
      className={className}
      initial="hidden"
      {...trigger}
      variants={{ show: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
      style={{ display: 'inline' }}
    >
      {words.map((w, i) => (
        <motion.span
          key={`${w}-${i}`}
          style={{ display: 'inline-block', marginRight: '0.28em' }}
          variants={{
            hidden: { opacity: 0, y: '0.5em' },
            show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
          }}
        >
          {w}
        </motion.span>
      ))}
    </motion.span>
  );
}

export default Reveal;
